/**
 * dragDropGovernor — the app-wide policy for EXTERNAL (Files) drags.
 *
 * Two problems, one listener pair:
 *
 * 1. NAVIGATION. A file dropped outside any element-level drop handler makes the browser
 *    NAVIGATE to the file. In the embedded WKWebView (file:// origin) that replaces the app
 *    with the raw image and destroys the bridge JS context — a hard wedge until Unity
 *    re-opens the panel. React attaches its listeners to the root container, so every
 *    element-level onDrop (FileLoader, ShotBar, AudioLane, ImportTab, the board tiles, the
 *    per-shot image drops) still runs during bubbling before these; only MISSED drops reach
 *    the governor, and it makes them silent no-ops.
 *
 * 2. THE GREEN "+". The original guard fixed (1) by preventDefault-ing every file `dragover`
 *    anywhere. But cancelling dragover without also refusing means "I accept this drop", so
 *    WebKit advertised a copy operation over the ENTIRE app — dragging a photo from Photos on
 *    iPad painted the green + badge over the Script tab and every dead area of Boards and
 *    Timeline, promising a drop that would never happen. The fix is to REFUSE
 *    (`dropEffect = 'none'`) everywhere the pointer is not over a live drop zone, and to stay
 *    completely hands-off where it is.
 *
 * The contract: a live drop zone is any element carrying `data-file-drop-target` (value
 * irrelevant). Components set it ONLY while their drop handlers are actually attached — e.g.
 * absent when readOnly/locked — so the badge tracks what will really accept the file.
 *
 * Hands-off matters literally: the zone's own dragover already ran (bubble phase) and set
 * `dropEffect = 'copy'`; dropEffect is last-writer-wins at the end of dispatch, so touching
 * the event here would undo the zone's acceptance.
 *
 * Files ONLY, on both events: an unconditional preventDefault on drop would also cancel the
 * browser's default text-insertion for drag-selected text into every textarea/input in the
 * app, and internal (element reorder) drags carry their own types.
 */

const ZONE_SELECTOR = '[data-file-drop-target]';

const overLiveZone = (e: Event): boolean =>
  e.target instanceof Element && e.target.closest(ZONE_SELECTOR) !== null;

const governDragOver = (e: DragEvent) => {
  const dt = e.dataTransfer;
  if (!dt?.types.includes('Files')) return;
  if (overLiveZone(e)) return; // the zone accepted it — do not overwrite its dropEffect
  e.preventDefault();
  dt.dropEffect = 'none';
};

const governDrop = (e: DragEvent) => {
  if (!e.dataTransfer?.types.includes('Files')) return;
  if (overLiveZone(e)) return;
  e.preventDefault(); // backstop: a drop that slipped through must not navigate the WebView
};

/** Installs the governor on `window` (bubble phase, so element handlers run first).
 *  Returns an uninstall function — used by the tests; production never uninstalls. */
export function installFileDragGovernor(): () => void {
  window.addEventListener('dragover', governDragOver);
  window.addEventListener('drop', governDrop);
  return () => {
    window.removeEventListener('dragover', governDragOver);
    window.removeEventListener('drop', governDrop);
  };
}
